import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { RiSearchLine } from "react-icons/ri"
import { BiDownload } from "react-icons/bi"
import { toast } from 'react-toastify'
import { saveAs } from 'file-saver'

import {
    Box,
    Grid,
    TextField,
    CardContent,
    FormControl,
} from '@mui/material'
import { getFirstDayOfPreviousMonth, getLastDayOfPreviousMonth } from '../../utils/dateUtils'

import * as yup from "yup"
import { yupResolver } from '@hookform/resolvers/yup'

import { CardStyled, DownloadButton } from '../Cfe/style'
import TitleCard from '../../components/Title'
import SelectStyled from '../../components/Select'
import Button from '../../components/DefaultButton'
import DatePicker from '../../components/DatePicker'
import {
    createNfesEstabelecimentoDownloadJob,
    downloadNfesEstabelecimentoJob,
    getNfesEstabelecimento,
    getNfesEstabelecimentoDownloadJob,
} from '../../services/estabelecimentoService'
import api from '../../services/api'
import TableEstabelecimento from '../../components/Tables/TableEstabelecimento'
import CircularProgress from '../../components/CircularProgress'
import CircularProgressDisabled from '../../components/CircularProgressDisabled'
import { Title } from '../../components/Title/style'

const validationSchema = yup.object({
    clienteId: yup.string().required("Selecione o cliente!"),
    dataDe: yup.date().required("Insira uma data!"),
})

export default function NfeEstabelecimento() {
    const pollTimeoutRef = useRef(null)
    const pollResolveRef = useRef(null)
    const downloadAttemptRef = useRef(0)
    const lastFiltersRef = useRef(null)
    const [nfes, setNfes] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [downloading, setDownloading] = useState(false)

    const [count, setCount] = useState(0)
    const [page, setPage] = useState(0)
    const [total, setTotal] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(0)

    const clearPollingTimeout = useCallback(() => {
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current)
            pollTimeoutRef.current = null
        }

        if (pollResolveRef.current) {
            pollResolveRef.current()
            pollResolveRef.current = null
        }
    }, [])

    const cancelDownloadJob = useCallback((updateState = true) => {
        downloadAttemptRef.current += 1
        clearPollingTimeout()

        if (updateState) {
            setDownloading(false)
        }
    }, [clearPollingTimeout])

    useEffect(() => {
        return () => {
            cancelDownloadJob(false)
        }
    }, [cancelDownloadJob])

    function createIdempotencyKey() {
        if (window.crypto?.randomUUID) {
            return window.crypto.randomUUID()
        }

        return `nfe-download-${Date.now()}-${Math.random().toString(16).slice(2)}`
    }

    function resolveDownloadUrl(downloadUrl) {
        if (!downloadUrl) {
            return null
        }

        if (/^https?:\/\//i.test(downloadUrl)) {
            return downloadUrl
        }

        const baseUrl = api.defaults.baseURL.replace(/\/$/, '')
        const path = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`
        return `${baseUrl}${path}`
    }

    function startNativeBrowserDownload(downloadUrl) {
        const resolvedUrl = resolveDownloadUrl(downloadUrl)

        if (!resolvedUrl) {
            throw new Error('URL de download não disponível')
        }

        window.location.assign(resolvedUrl)
    }

    function getFileNameFromDisposition(contentDisposition) {
        if (!contentDisposition) {
            return 'nfes.zip'
        }

        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
        if (utf8Match?.[1]) {
            return decodeURIComponent(utf8Match[1])
        }

        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
        return fileNameMatch?.[1] || 'nfes.zip'
    }

    async function waitForNfesDownloadJob(clienteId, jobId, attemptId) {
        while (downloadAttemptRef.current === attemptId) {
            const response = await getNfesEstabelecimentoDownloadJob(clienteId, jobId)
            const job = response.data

            if (job.status === 'COMPLETED') {
                return job
            }

            if (job.status === 'FAILED') {
                throw new Error(job.errorMessage || 'Falha ao gerar o arquivo para download')
            }

            await new Promise((resolve) => {
                pollResolveRef.current = resolve
                pollTimeoutRef.current = setTimeout(() => {
                    pollTimeoutRef.current = null
                    pollResolveRef.current = null
                    resolve()
                }, 3000)
            })
        }

        throw new Error('Download cancelado')
    }

    async function onSubmit(values, page = 0) {
        cancelDownloadJob()
        setNfes([])
        setIsLoading(true)
        try {
            const { clienteId, dataDe, dataAte } = values
            const response = await getNfesEstabelecimento(clienteId, dataDe, dataAte, page)
            const _nfes = response.data.content
            setPage(page);
            setNfes(_nfes)
            setCount(response.data.totalElements)
            setTotal(response.data.total)
            setRowsPerPage(response.data.size)
        } catch (error) {
            toast.error(error.response?.data?.message || error.message)
        } finally {
            setIsLoading(false)
        }
    }

    async function onDownload(values) {
        const attemptId = downloadAttemptRef.current + 1
        downloadAttemptRef.current = attemptId
        setDownloading(true)
        clearPollingTimeout()
        try {
            const { clienteId, dataDe, dataAte } = values
            const idempotencyKey = createIdempotencyKey()
            const createJobResponse = await createNfesEstabelecimentoDownloadJob(clienteId, dataDe, dataAte, idempotencyKey)
            const job = await waitForNfesDownloadJob(clienteId, createJobResponse.data.id, attemptId)

            if (downloadAttemptRef.current !== attemptId) {
                return
            }

            if (job.downloadUrl) {
                startNativeBrowserDownload(job.downloadUrl)
                return
            }

            const response = await downloadNfesEstabelecimentoJob(clienteId, job.id)
            const fileName = getFileNameFromDisposition(response.headers["content-disposition"])
            saveAs(response.data, fileName)
        } catch (error) {
            if (error.message !== 'Download cancelado') {
                toast.error(error.response?.data?.message || error.message || 'Arquivo indisponível. Gere o download novamente.')
            }
        } finally {
            if (downloadAttemptRef.current === attemptId) {
                clearPollingTimeout()
                setDownloading(false)
            }
        }
    }

    const { control, handleSubmit, watch, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            clienteId: "",
            dataDe: getFirstDayOfPreviousMonth(),
            dataAte: getLastDayOfPreviousMonth()
        }
    })

    const filters = watch(['clienteId', 'dataDe', 'dataAte'])

    useEffect(() => {
        const nextFilters = JSON.stringify(filters)
        if (lastFiltersRef.current === null) {
            lastFiltersRef.current = nextFilters
            return
        }

        if (lastFiltersRef.current !== nextFilters) {
            lastFiltersRef.current = nextFilters
            cancelDownloadJob()
        }
    }, [cancelDownloadJob, filters])

    return (
        <FormControl
            onSubmit={handleSubmit((values) => onSubmit(values))}
            sx={{
                width: '100%',
            }}
        >
            <CardStyled>
                <CardContent>
                    <Box
                        component="form"
                    >
                        <Grid
                            container
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <TitleCard
                                pageName="NF-e Estabelecimento"
                                description="(Nota Fiscal Eletrônica - Estabelecimento)"
                            />
                            <Grid
                                container
                                spacing={2}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    padding: '1em 0'
                                }}
                            >
                                <Grid
                                    item
                                    sm={5}
                                >
                                    <Controller
                                        control={control}
                                        name="clienteId"
                                        render={({ field }) => (
                                            <SelectStyled
                                                value={field.value}
                                                onChange={(e) => {
                                                    setNfes([])
                                                    field.onChange(e)
                                                }}
                                                inputRef={field.ref}
                                                displayEmpty
                                                label="Clientes"
                                                error={!!errors.clienteId}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    sm={5}
                                >
                                    <DatePicker
                                        control={control}
                                        errors={errors}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    sm={2}
                                >
                                    <Button
                                        type="submit"
                                        label="Buscar"
                                        startIcon={
                                            <RiSearchLine />
                                        }
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={4}
                                    sm={4}
                                    sx={{
                                        paddingTop: '0px!important',
                                        color: 'red'
                                    }}
                                >
                                    <span>
                                        {errors.clienteId?.message}
                                    </span>
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    sm={6}
                                    sx={{
                                        paddingTop: '0px!important',
                                        color: 'red'
                                    }}
                                >
                                    <span>
                                        {errors.dataDe && <span>Insira uma data de início e fim!</span>}
                                    </span>
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                sm={2}
                            >
                                <DownloadButton
                                    label="Download"
                                    onClick={handleSubmit(onDownload)}
                                    startIcon={
                                        downloading ? <CircularProgressDisabled /> : <BiDownload />
                                    }
                                    disabled={nfes.length === 0 || downloading}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </CardStyled>
            {
                isLoading && (<CircularProgress />)
            }
            {
                nfes.length === 0 &&
                <Grid
                    container
                >
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {
                            !isLoading ?
                                <Title>
                                    Nenhum registro encontrado...
                                </Title> :
                                null
                        }
                    </Grid>
                </Grid>
            }

            {
                nfes.length > 0 &&
                <TableEstabelecimento
                    nfes={nfes}
                    count={count}
                    page={page}
                    total={total}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(event, nextPage) => {
                        handleSubmit((values) => onSubmit(values, nextPage))()
                    }}
                />
            }
        </FormControl>
    )
}
